/**
 * Functions page — Catalog of all EXCELAI custom functions.
 *
 * This page is opened by the "Fonctions Excel IA" ribbon button (#functions).
 * It lists all available =EXCELAI.* custom functions with syntax,
 * descriptions, and usage examples.
 */

import React from 'react';
import {
  Text,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';

interface FunctionInfo {
  name: string;
  syntax: string;
  description: string;
  example: string;
}

const CUSTOM_FUNCTIONS: FunctionInfo[] = [
  {
    name: 'SUMMARIZE',
    syntax: '=EXCELAI.SUMMARIZE(texte)',
    description: 'Resume un texte en une phrase concise.',
    example: '=EXCELAI.SUMMARIZE(A1)',
  },
  {
    name: 'TRANSLATE',
    syntax: '=EXCELAI.TRANSLATE(texte; [source]; [cible])',
    description: 'Traduit un texte. Source auto-detectee, cible EN par defaut.',
    example: '=EXCELAI.TRANSLATE(A1; "FR"; "EN")',
  },
  {
    name: 'EXPLAIN',
    syntax: '=EXCELAI.EXPLAIN(texte)',
    description: 'Explique un texte ou une formule Excel en langage clair.',
    example: '=EXCELAI.EXPLAIN("=VLOOKUP(A1;B:C;2;0)")',
  },
  {
    name: 'CLASSIFY',
    syntax: '=EXCELAI.CLASSIFY(texte)',
    description: 'Classe un texte dans une categorie unique.',
    example: '=EXCELAI.CLASSIFY(A1)',
  },
  {
    name: 'SENTIMENT',
    syntax: '=EXCELAI.SENTIMENT(texte)',
    description: 'Detecte le sentiment : positive, negative ou neutral.',
    example: '=EXCELAI.SENTIMENT(A1)',
  },
  {
    name: 'EXTRACT_EMAIL',
    syntax: '=EXCELAI.EXTRACT_EMAIL(texte)',
    description: 'Extrait les adresses email contenues dans un texte.',
    example: '=EXCELAI.EXTRACT_EMAIL(A1)',
  },
  {
    name: 'ANALYZE',
    syntax: '=EXCELAI.ANALYZE(texte)',
    description: 'Fournit une analyse succincte de donnees ou texte.',
    example: '=EXCELAI.ANALYZE(A1)',
  },
  {
    name: 'STANDARDIZE',
    syntax: '=EXCELAI.STANDARDIZE(texte; [mode])',
    description: 'Standardise une valeur texte (trim, casse, espaces). Modes: auto, name, email, phone, country, city, text.',
    example: '=EXCELAI.STANDARDIZE(A1; "name")',
  },
  {
    name: 'EXTRACT_ENTITY',
    syntax: '=EXCELAI.EXTRACT_ENTITY(texte; type)',
    description: 'Extrait une entite specifique d\'un texte. Types: email, phone, company, person, date, country, city.',
    example: '=EXCELAI.EXTRACT_ENTITY(A1; "email")',
  },
  {
    name: 'INSIGHT',
    syntax: '=EXCELAI.INSIGHT(texte; [domaine])',
    description: 'Genere une analyse succincte et exploitable. Domaines: general, sales, hr, support, finance.',
    example: '=EXCELAI.INSIGHT(A1; "sales")',
  },
  {
    name: 'CLEAN',
    syntax: '=EXCELAI.CLEAN(texte; [mode])',
    description: 'Nettoie et normalise une valeur texte. Modes: auto, text, name, email, phone, country, city.',
    example: '=EXCELAI.CLEAN(A1; "email")',
  },
  {
    name: 'DETECT_SCHEMA',
    syntax: '=EXCELAI.DETECT_SCHEMA(plage)',
    description: 'Retourne un resume lisible de la structure d\'un dataset (colonnes, types, dimensions).',
    example: '=EXCELAI.DETECT_SCHEMA(A1:F50)',
  },
  {
    name: 'OUTLIER_FLAG',
    syntax: '=EXCELAI.OUTLIER_FLAG(valeur; plage)',
    description: 'Indique si une valeur est une anomalie dans une plage donnee (OUTLIER ou OK).',
    example: '=EXCELAI.OUTLIER_FLAG(D2; D$2:D$100)',
  },
  {
    name: 'GENERATE_FORMULA',
    syntax: '=EXCELAI.GENERATE_FORMULA(description; [langue])',
    description: 'Genere une formule Excel a partir d\'une description en langage naturel. Langues: en, fr, de, es, it.',
    example: '=EXCELAI.GENERATE_FORMULA("moyenne de A1 a A10"; "fr")',
  },
];

export const FunctionsPage: React.FC = () => (
  <div>
    <div className="card">
      <Text className="section-title">Fonctions Excel IA</Text>
      <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
        Toutes les fonctions utilisent le namespace{' '}
        <Badge appearance="tint" color="brand" size="small">EXCELAI</Badge>.
        Tapez <code>=EXCELAI.</code> dans une cellule pour voir l&apos;autocompletion.
      </Text>
      <MessageBar intent="warning" style={{ borderRadius: 4, marginTop: 6 }}>
        <MessageBarBody>
          Compatibilite Office: les fonctions <code>=EXCELAI.*</code> ne sont pas disponibles sur toutes les editions.
          Sur certaines versions (ex: LTSC), vous pouvez avoir <code>#NOM?</code> meme si le panneau Excel AI Studio fonctionne.
        </MessageBarBody>
      </MessageBar>
    </div>

    <div className="card">
      <MessageBar intent="info" style={{ borderRadius: 4, marginBottom: 12 }}>
        <MessageBarBody>
          Les fonctions utilisent le fournisseur IA et la cle API configures dans l&apos;onglet Parametres.
          Si la cle est manquante, la cellule affichera <code>#CONFIG</code>.
        </MessageBarBody>
      </MessageBar>

      <div style={{ overflowX: 'auto' }}>
        <Table size="extra-small" aria-label="Custom functions reference">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Fonction</TableHeaderCell>
              <TableHeaderCell>Description</TableHeaderCell>
              <TableHeaderCell>Exemple</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {CUSTOM_FUNCTIONS.map((fn) => (
              <TableRow key={fn.name}>
                <TableCell>
                  <Text size={200} weight="semibold" style={{ fontFamily: 'monospace' }}>
                    {fn.name}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text size={200}>{fn.description}</Text>
                </TableCell>
                <TableCell>
                  <Text size={200} style={{ fontFamily: 'monospace', color: 'var(--colorBrandForeground1)' }}>
                    {fn.example}
                  </Text>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>

    <div className="card">
      <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: 6 }}>Syntaxe detaillee</Text>
      {CUSTOM_FUNCTIONS.map((fn) => (
        <Text key={fn.name} size={200} style={{ fontFamily: 'monospace', display: 'block', marginBottom: 3 }}>
          {fn.syntax}
        </Text>
      ))}
    </div>

    <div className="card">
      <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: 6 }}>Codes d&apos;erreur</Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <ErrorLine code="#CONFIG" description="Cle API non configuree. Ouvrez Parametres." />
        <ErrorLine code="#ERREUR: Delai depasse" description="Le serveur n'a pas repondu dans le delai (60s)." />
        <ErrorLine code="#ERREUR: Backend inaccessible" description="Le backend n'est pas joignable." />
      </div>
    </div>

    <div className="card">
      <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: 6 }}>
        Que faire si <code>#NOM?</code> avec <code>=EXCELAI.*</code> ?
      </Text>
      <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <li>
          <Text size={200}>
            Verifiez d&apos;abord la compatibilite Office. Sur certaines editions (ex: LTSC), les Custom Functions peuvent ne pas etre disponibles.
          </Text>
        </li>
        <li>
          <Text size={200}>
            Si votre edition est compatible, redemarrez Excel puis recharger le complement (manifest) pour relancer le runtime des fonctions.
          </Text>
        </li>
        <li>
          <Text size={200}>
            En attendant, utilisez les modules du panneau (Assistant, Formules, Nettoyage, Analyse), qui restent pleinement utilisables.
          </Text>
        </li>
      </ol>
    </div>
  </div>
);

const ErrorLine: React.FC<{ code: string; description: string }> = ({ code, description }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
    <Badge size="small" appearance="tint" color="danger" style={{ fontFamily: 'monospace', flexShrink: 0 }}>
      {code}
    </Badge>
    <Text size={200}>{description}</Text>
  </div>
);
